import { getAccessToken, Text } from "@hackthenorth/north";
import moment from "moment";
import { Button, TextInput } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Header from "src/shared/components/Header";
import PageWrapper from "src/shared/components/PageWrapper";
import { useUserContext } from "src/shared/contexts";
import { API_HOST_URL } from "src/shared/utils/env";
import { BadgeProvisioner } from "src/views/volunteer/registration/badge-registration/badgeProvisioner";

import AdminBadgeTools from "./AdminBadgeTools";
import { useGetBadgeBackupQuery } from "./graphql/getBadgeBackup.generated";
import SyncSummary from "./SyncSummary";

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 760px;
  p {
    display: block;
    margin: 0;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 420px;
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 400px;
    overflow: auto;
    padding: 16px;
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
    font-family: ${({ theme }) => theme.globalConstants.fontFamily.code};
    font-size: 12px;
  }
`;
const SyncStatus = styled.div<{ $error: boolean }>`
  padding: ${({ $error }) => ($error ? "16px" : "0")};
  border-radius: 8px;
  color: ${({ $error }) => ($error ? "#991b1b" : "inherit")};
  background: ${({ $error }) => ($error ? "#fef2f2" : "transparent")};
  border: ${({ $error }) => ($error ? "1px solid #ef4444" : "none")};
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
const SyncNotice = styled.aside`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderWarning};
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
`;

export default function BadgeSync({ admin = false }: { admin?: boolean }) {
  const { isOrganizer, id } = useUserContext();
  const provisioner = useRef<BadgeProvisioner | null>(null);
  // Credentials stay in memory, outside the rendered preview and browser storage.
  const upload = useRef<AwaitedUpload | null>(null);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [preview, setPreview] = useState("");
  const [target, setTarget] = useState("");
  const [message, setMessage] = useState("Connect your badge to get started.");
  const [error, setError] = useState(false);
  const [savedBackup, setSavedBackup] = useState<{
    userId: number;
    value: string;
    updated_at: string;
  } | null>(null);
  const {
    data: backupData,
    loading: backupLoading,
    error: backupError,
    refetch: refetchBackup,
  } = useGetBadgeBackupQuery({
    variables: { userId: id as number },
    skip: !id || admin,
    // Snapshots can contain private identity/contact/wallet data. Keep them
    // out of the shared Apollo cache, as on the Connect page.
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });
  const fetchedBackup =
    backupData?.claims[0]?.user_id === id
      ? backupData.claims[0].fields_HACKTHENORTH2026_APPLICATIONS.answers
          .badge_data_json
      : null;
  const backup =
    savedBackup?.userId === id &&
    (!fetchedBackup ||
      Date.parse(savedBackup.updated_at) > Date.parse(fetchedBackup.updated_at))
      ? savedBackup
      : fetchedBackup;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const device = new BadgeProvisioner({
      onSyncDiagnostic: (text) => console.info("[Badge sync]", text),
      onConnectionLost: () => {
        upload.current = null;
        setConnected(false);
        setPreview("");
        setTarget("");
        setMessage("Badge disconnected. Connect it again to sync.");
      },
    });
    provisioner.current = device;
    return () => {
      upload.current = null;
      void device.disconnect();
    };
  }, []);

  useEffect(() => {
    upload.current = null;
    setPreview("");
    setTarget("");
    try {
      setPassword(
        admin && isOrganizer
          ? localStorage.getItem(`badge-sync:station-password:${id}`) || ""
          : ""
      );
    } catch {
      setPassword("");
    }
    setSavedBackup(null);
  }, [id, admin, isOrganizer]);

  if (admin && !isOrganizer)
    return (
      <PageWrapper pageTitle="Hacker Badge: Admin Sync">
        <Text>Organizer access is required.</Text>
      </PageWrapper>
    );

  const save = async (data: AwaitedUpload) => {
    const authorization = admin ? password : getAccessToken();
    if (!authorization)
      throw new Error(
        admin ? "Enter the station password." : "Sign in again before syncing."
      );
    setMessage("Backing up your badge and getting your latest profile…");
    console.info("[Badge sync]", "Uploading snapshot.");
    let response: Response;
    try {
      response = await fetch(
        `${API_HOST_URL}/v3/badge-data/${admin ? "upload" : "self-upload"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(admin
              ? { password: authorization, claim_id: data.claimId }
              : { user_token: authorization }),
            badge_secure_token: data.rawToken,
            badge_data: data.snapshot,
          }),
          credentials: "omit",
          redirect: "error",
          cache: "no-store",
        }
      );
    } catch {
      console.warn("[Badge sync]", "Could not reach the sync service.");
      throw new Error(
        "Could not reach the sync service. Check your connection and try again."
      );
    }
    console.info("[Badge sync]", `Upload response: HTTP ${response.status}.`);
    if (!response.ok)
      throw new Error(
        response.status === 403
          ? "This badge could not be verified as yours. Check that you connected your own badge. If it is yours, bring it to the Badge Help Desk so we can fix its registration."
          : response.status === 401
          ? admin
            ? "The station password was not accepted."
            : "Your session expired. Sign in again."
          : response.status === 422
          ? "Your profile does not fit on the badge. Update Badge Info before syncing."
          : "Sync could not be completed. Reconnect and try again, or ask registration for help."
      );
    const result = await response.json();
    if (result.status !== "success")
      throw new Error("The server did not confirm the backup. Retry Sync.");
    if (!admin) {
      // Only show new records after the API confirms the backup was saved.
      // A later profile-write failure does not undo that successful backup.
      setSavedBackup({
        userId: id as number,
        value: JSON.stringify(data.snapshot),
        updated_at: new Date().toISOString(),
      });
      setNow(Date.now());
      void refetchBackup().catch(() => undefined);
    }
    setMessage(
      "Backup saved. Updating your badge profile… Keep the badge connected."
    );
    try {
      if (!result.profile) throw new Error("Missing profile response");
      console.info("[Badge sync]", "Applying the returned badge profile.");
      await provisioner.current!.applyProfile(result.profile);
    } catch {
      console.warn(
        "[Badge sync]",
        "Badge profile update could not be verified."
      );
      throw new Error(
        "Your backup was saved, but the profile update could not be verified on the badge. Reconnect with the latest firmware and retry Sync."
      );
    }
    upload.current = null;
    setPreview("");
    setTarget("");
    if (!admin) setPassword("");
    setMessage(
      "Sync successful. Your badge is backed up and its profile is up to date. You can unplug the badge."
    );
    console.info("[Badge sync]", "Sync completed successfully.");
  };

  return (
    <>
      <Header
        title={admin ? "Hacker Badge: Admin Sync" : "Hacker Badge: Sync"}
        subtitle="Back up your hacker badge"
      />
      <PageWrapper>
        <Panel
          data-private="true"
          data-hj-suppress="true"
          className="ph-no-capture"
        >
          <Text mods="h3 heading">
            {admin ? "Review and save a badge" : "Save your badge data"}
          </Text>
          <Text>
            {admin
              ? "Connect a badge, preview its data, then save. The same sync also updates the badge with its latest dashboard profile."
              : "Plug in your badge, connect, and press Sync to back it up and update its profile."}
          </Text>
          <SyncNotice role="note">
            <Text>
              Make sure your badge is switched <strong>OFF</strong> while
              plugged in via USB for sync to work.
            </Text>
            <Text>
              This page restarts the badge automatically before Sync or Read
              badge. Keep it connected until the operation finishes.
            </Text>
            <Text>
              Choose the <strong>USB JTAG/serial debug unit</strong> when
              prompted.
            </Text>
            <Text>
              If requested, give your browser permission to communicate with
              devices.
            </Text>
          </SyncNotice>
          {!admin && (
            <Text mods="grey" aria-live="polite">
              Last synced:{" "}
              {backup && Date.parse(backup.updated_at) > 0 ? (
                <time
                  dateTime={backup.updated_at}
                  title={moment(backup.updated_at).format("lll")}
                >
                  {moment(backup.updated_at).format("MMM D, h:mm A")} ·{" "}
                  {moment(backup.updated_at).from(now)}
                </time>
              ) : backupLoading ? (
                "Loading…"
              ) : backupError ? (
                "Unavailable"
              ) : (
                "Never"
              )}
            </Text>
          )}
          {!admin && backupError && (
            <Button
              size="sm"
              color="secondary"
              disabled={backupLoading}
              onClick={() => void refetchBackup().catch(() => undefined)}
            >
              Retry sync status
            </Button>
          )}
          {!BadgeProvisioner.isSupported() && (
            <Text role="alert">
              Use desktop Chrome or Edge on HTTPS or localhost to connect a USB
              badge.
            </Text>
          )}
          {admin && (
            <label htmlFor="station-password">
              <Text mods="bold textBody">Station password</Text>
              <TextInput
                id="station-password"
                size="md"
                type="password"
                autoComplete="off"
                required
                value={password}
                onChange={(event) => {
                  const value = event.target.value;
                  setPassword(value);
                  try {
                    const key = `badge-sync:station-password:${id}`;
                    if (value) localStorage.setItem(key, value);
                    else localStorage.removeItem(key);
                  } catch {
                    /* Storage can be disabled; keep using the in-memory value. */
                  }
                }}
              />
              <Text mods="grey">
                Remembered in this browser. Clear the field to forget it.
              </Text>
            </label>
          )}
          <Actions>
            <Button
              color="secondary"
              size="md"
              disabled={busy || !BadgeProvisioner.isSupported()}
              onClick={async () => {
                setBusy(true);
                setError(false);
                upload.current = null;
                setPreview("");
                setTarget("");
                try {
                  if (connected) {
                    await provisioner.current?.disconnect();
                    setConnected(false);
                    setMessage("Badge disconnected.");
                  } else {
                    await provisioner.current?.connect();
                    setConnected(true);
                    setMessage("Badge connected. Ready to sync.");
                  }
                } catch {
                  setError(true);
                  setMessage(
                    "Could not connect. Select your badge and close any other serial tools."
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {connected ? "Disconnect" : "Connect badge"}
            </Button>
            <Button
              color="primary"
              size="md"
              disabled={!connected || busy}
              onClick={async () => {
                setBusy(true);
                setError(false);
                upload.current = null;
                setPreview("");
                setTarget("");
                setMessage("Restarting badge before sync… Keep it connected.");
                try {
                  const ownClaim = backupData?.claims.find(
                    (claim) => claim.user_id === id
                  );
                  if (!admin && !ownClaim)
                    throw new Error(
                      "We could not load your badge registration. Reload the page and try again, or visit the Badge Help Desk."
                    );
                  await provisioner.current!.rebootForSync();
                  setMessage("Reading badge data… Keep the badge connected.");
                  const data = await provisioner.current!.readUpload(
                    admin ? undefined : ownClaim!.id
                  );
                  if (admin) {
                    upload.current = data;
                    setTarget(
                      `${data.displayName} · ${data.badgeId} · Claim #${data.claimId}`
                    );
                    setPreview(JSON.stringify(data.snapshot, null, 2));
                    setMessage(
                      "Preview ready. Review the badge data, then save to back it up and apply the latest dashboard profile."
                    );
                  } else {
                    await save(data);
                  }
                } catch (failure) {
                  setError(true);
                  setMessage(
                    failure instanceof Error
                      ? failure.message
                      : "Sync failed. Reconnect and try again."
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Working…" : admin ? "Read badge" : "Sync"}
            </Button>
          </Actions>
          <SyncStatus
            $error={error}
            role={error ? "alert" : "status"}
            aria-live="polite"
          >
            {message}
            {error && (
              <p>
                Unplug your badge and plug it back in. Reconnect on this page,
                then sync before pressing any buttons on the badge.
              </p>
            )}
          </SyncStatus>
          {!admin &&
            (backup ? (
              <SyncSummary value={backup.value} />
            ) : backupLoading ? (
              <Text>Loading saved activity…</Text>
            ) : (
              !backupError && <SyncSummary />
            ))}
          {admin && preview && (
            <>
              <Text mods="h3 heading">Destination read from badge</Text>
              <Text>{target}</Text>
              <Text>
                Saving backs up this snapshot to the claim shown above. The API
                returns the latest dashboard profile, which is then applied to
                the badge.
              </Text>
              <details open>
                <summary>Preview snapshot</summary>
                <pre>{preview}</pre>
              </details>
              <Button
                color="primary"
                size="md"
                disabled={busy || !password || !connected}
                onClick={async () => {
                  if (!upload.current) return;
                  setBusy(true);
                  setError(false);
                  try {
                    await save(upload.current);
                  } catch (failure) {
                    setError(true);
                    setMessage(
                      failure instanceof Error
                        ? failure.message
                        : "Save failed. Try again."
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save badge data
              </Button>
            </>
          )}
          {admin && (
            <AdminBadgeTools
              key={id ?? "signed-out"}
              device={provisioner.current}
              connected={connected}
              busy={busy}
              setBusy={setBusy}
              invalidateUpload={() => {
                upload.current = null;
                setPreview("");
                setTarget("");
              }}
              onDisconnect={() => {
                setConnected(false);
                upload.current = null;
                setPreview("");
                setTarget("");
              }}
            />
          )}
        </Panel>
      </PageWrapper>
    </>
  );
}

type AwaitedUpload = ReturnType<BadgeProvisioner["readUpload"]> extends Promise<
  infer T
>
  ? T
  : never;
